// CountReview (truck stock, BL-203): a tech's count of his van, beside what the books show, for the office
// to post. The count is blind (the tech never saw the books' figures), so the office sees both: what it
// shows, what he counted, the difference, and, under each line that differs, what the jobs and restocks did
// since its last count. Each difference needs a reason (a select of reasons) or a recount asked of him,
// with a note; an item he found that isn't on his van's list is put on it or brought back to the shop. Post
// sets on hand to what he counted; it says what's missing first (ink words after StateMark's red bar: red
// is never small text here), the field's own line under it, and takes the focus there. Posted, it says what
// changed and shows his reply. The lines that match wait behind Show all. At our cost, for whoever sees our
// cost. Narrow (a phone), why each is off sits under its line, not in a column. Built from LineTable
// (groups and notes), Field, ChoiceGroup, Link, Button and Notice. Reads only --countrev-*.
// New (Domain/Stock): LineTable draws its lines; what's new is a count's rules (a reason or a recount for
// each difference, the item not on the list, Post checking them first).
import { useId, useState, type ReactNode } from 'react';
import { money } from '../../format.ts';
import { Button } from '../Button/Button.tsx';
import { Choice, ChoiceGroup } from '../ChoiceGroup/ChoiceGroup.tsx';
import { SelectField, TextField } from '../Field/Field.tsx';
import { LineTable, type LineColumn, type LineGroup, type LineRow } from '../LineTable/LineTable.tsx';
import { Link } from '../Link/Link.tsx';
import { Notice } from '../Notice/Notice.tsx';
import { StateMark } from '../StateMark/StateMark.tsx';
import { offWords, qtyWords } from '../stock-words.ts';
import './CountReview.css';

export interface CountLine {
  itemId: string;
  itemName: string;
  code: string;
  /** The unit it's counted in: "each", "ft". */
  unit: string;
  category: { id: string; name: string };
  /** What the books show on the van. */
  shows: string;
  /** What the tech counted. */
  counted: string;
  /** Flagged for a count by a move: when it matches, the flag clears. */
  flagged?: boolean;
  /** What the books did since its last count: "Counted 100 on 19 Oct; −12 on J-24611". */
  trail?: string;
  /** What the jobs explain; null: nothing does (it adds up to what it shows). */
  known?: string | null;
  /** One at our cost: only for whoever sees our cost. */
  eachCents?: number;
}
/** Something he counted that isn't on his van's list. */
export interface CountExtra {
  itemId: string;
  itemName: string;
  code: string;
  unit: string;
  counted: string;
  eachCents?: number;
}
export type ExtraChoice = 'add' | 'back';
export interface CountDecision {
  /** A reason for each line that differs, by item. */
  reasons: Record<string, string>;
  /** A recount asked, with the note to the tech, by item. */
  recounts: Record<string, string>;
  /** Where each extra goes. */
  extra: Record<string, ExtraChoice>;
}
export interface CountReviewProps {
  /** The van: "Sam’s van". */
  place: string;
  /** Whose count: "Sam K.", and how the words call him: "Sam". */
  tech: string;
  first: string;
  /** When it came: "10:52 a.m.". */
  sentAt: string;
  lines: CountLine[];
  extra?: CountExtra[];
  /** His note with the count. */
  note?: string;
  /** Who posts it: "Chris B.". */
  postedBy: string;
  reasons?: readonly string[];
  /** Where the work starts: reasons the jobs explain, a recount already asked, all lines shown, Post tried, posted. */
  initial?: Partial<CountDecision> & { showAll?: boolean; tried?: boolean; postedAt?: string | null };
  /** The time it posts at, in words. */
  now?: () => string;
  /** The note a recount starts with. */
  recountNote?: string;
  onPost?: (d: CountDecision) => void;
  onNotNow?: () => void;
  /** Narrow (a phone): why each is off sits under its line, across the table, not in a column. */
  narrow?: boolean;
}

export const COUNT_REASONS = [
  'Used, not logged',
  'Logged, not used',
  'Damaged or thrown out',
  'Found',
  'Counted wrong last time',
] as const;

const off = (l: { shows: string; counted: string }) =>
  Math.round((Number(l.counted) - Number(l.shows)) * 1000) / 1000;
const signed = (c: number) => `${c < 0 ? '−' : '+'}${money(Math.abs(c))}`;
const Wait = ({ children }: { children: ReactNode }) => <span className="fl-countrev__wait">{children}</span>;
const clock = () =>
  new Date()
    .toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })
    .replace('AM', 'a.m.')
    .replace('PM', 'p.m.');

export function CountReview({
  place,
  tech,
  first,
  sentAt,
  lines,
  extra = [],
  note,
  postedBy,
  reasons = COUNT_REASONS,
  initial = {},
  now = clock,
  recountNote = 'The restock and your jobs don’t add up for these. Can you count them again?',
  onPost,
  onNotNow,
  narrow,
}: CountReviewProps) {
  const uid = useId();
  const [why, setWhy] = useState<Record<string, string>>(initial.reasons ?? {});
  const [recount, setRecount] = useState<Record<string, string>>(initial.recounts ?? {});
  const [ext, setExt] = useState<Record<string, ExtraChoice>>(initial.extra ?? {});
  const [all, setAll] = useState(!!initial.showAll);
  const [tried, setTried] = useState(!!initial.tried);
  const [postedAt, setPostedAt] = useState<string | null>(initial.postedAt ?? null);
  const cost = lines.some((l) => l.eachCents !== undefined);
  const diffs = lines.filter((l) => off(l) !== 0);
  const posted = postedAt !== null;
  const idOf = (kind: string, item: string) => `${uid}-${kind}-${item}`;

  // what Post needs first, in order: a reason or a recount's note for each difference, then each extra
  const problems: { id: string; item: string; msg: string; field: string }[] = [];
  for (const l of diffs) {
    const rc = recount[l.itemId];
    if (rc !== undefined) {
      if (!rc.trim())
        problems.push({
          id: idOf('note', l.itemId),
          item: l.itemId,
          field: 'Say what to count again',
          msg: `Say what ${first} should count again (${l.itemName})`,
        });
    } else if (!why[l.itemId])
      problems.push({
        id: idOf('why', l.itemId),
        item: l.itemId,
        field: 'Pick a reason, or ask a recount',
        msg: `Pick why ${l.itemName} is off, or ask ${first} to count it again`,
      });
  }
  for (const e of extra)
    if (!ext[e.itemId])
      problems.push({
        id: idOf('extra', e.itemId),
        item: e.itemId,
        field: 'Say where it goes',
        msg: `Say where the ${e.itemName} goes: on ${place}, or back to the shop`,
      });
  const errorFor = (item: string) => (tried ? problems.find((p) => p.item === item)?.field : undefined);

  const post = () => {
    setTried(true);
    const next = problems[0];
    if (next) {
      document.getElementById(next.id)?.querySelector<HTMLElement>('input, select, textarea')?.focus();
      return;
    }
    const at = now();
    setPostedAt(at);
    onPost?.({ reasons: why, recounts: recount, extra: ext });
  };

  const columns: LineColumn[] = [
    { id: 'item', label: 'Item' },
    { id: 'shows', label: 'Shows', align: 'end' },
    { id: 'counted', label: `${first} counted`, align: 'end' },
    { id: 'off', label: 'Off by', align: 'end' },
    ...(narrow ? [] : [{ id: 'why', label: 'Why' }]),
    ...(cost ? [{ id: 'cost', label: 'At cost', align: 'end' } as const] : []),
  ];
  const item = (name: string, code: string) => (
    <span className="fl-countrev__item">
      <span className="fl-countrev__name">{name}</span>
      <span className="fl-countrev__code">{code}</span>
    </span>
  );
  const whyCell = (l: CountLine): ReactNode => {
    if (off(l) === 0) return null;
    const rc = recount[l.itemId];
    if (posted) return rc !== undefined ? <Wait>Recount asked</Wait> : why[l.itemId];
    if (rc !== undefined)
      return (
        <div className="fl-countrev__why">
          <span className="fl-countrev__asked">
            <Wait>Recount asked</Wait>
            <Link
              onPress={() =>
                setRecount(({ [l.itemId]: _gone, ...rest }) => {
                  void _gone;
                  return rest;
                })
              }
            >
              Undo
            </Link>
          </span>
          <div className="fl-countrev__field" id={idOf('note', l.itemId)}>
            <TextField
              label={`Note to ${first}`}
              value={rc}
              onChange={(v) => setRecount((r) => ({ ...r, [l.itemId]: v }))}
              error={errorFor(l.itemId)}
            />
          </div>
        </div>
      );
    return (
      <div className="fl-countrev__why">
        <div className="fl-countrev__field" id={idOf('why', l.itemId)}>
          <SelectField
            label={`Why ${l.itemName} is off`}
            hideLabel
            value={why[l.itemId] ?? ''}
            onChange={(v) => setWhy((w) => ({ ...w, [l.itemId]: v }))}
            options={[{ value: '', label: 'Why it’s off?' }, ...reasons.map((r) => ({ value: r, label: r }))]}
            error={errorFor(l.itemId)}
          />
        </div>
        <Link onPress={() => setRecount((r) => ({ ...r, [l.itemId]: recountNote }))}>Ask a recount</Link>
      </div>
    );
  };
  // narrow: the why (or where it goes) moves out of its column into the note under the line
  const under = (why: ReactNode, said: ReactNode) =>
    narrow && why != null ? (
      <div className="fl-countrev__under">
        {why}
        {said != null ? <span className="fl-countrev__said">{said}</span> : null}
      </div>
    ) : (
      said
    );
  const row = (l: CountLine): LineRow => {
    const by = off(l);
    const said =
      by !== 0 && l.trail ? (
        <>
          {l.trail}.{' '}
          <strong className="fl-countrev__known">
            {l.known ? `${l.known}.` : 'That adds up to what it shows: nothing explains it.'}
          </strong>
        </>
      ) : l.flagged && by === 0 ? (
        'Flagged for a count: it matches, so the flag clears.'
      ) : undefined;
    return {
      id: l.itemId,
      cells: {
        item: item(l.itemName, l.code),
        shows: qtyWords(l.shows, l.unit),
        counted: <strong>{qtyWords(l.counted, l.unit)}</strong>,
        off:
          by === 0
            ? { tone: 'none', value: '0' }
            : by < 0
              ? { tone: 'problem', value: <strong>{offWords(by, l.unit)}</strong> }
              : offWords(by, l.unit),
        ...(narrow ? {} : { why: whyCell(l) }),
        ...(cost ? { cost: by && l.eachCents !== undefined ? signed(by * l.eachCents) : null } : {}),
      },
      note: under(narrow ? whyCell(l) : null, said),
    };
  };
  const extraRows: LineRow[] = extra.map((e) => {
    const said =
      ext[e.itemId] === 'add'
        ? `Goes on ${place}’s list: ${qtyWords(e.counted, e.unit)} on hand, no min yet.`
        : ext[e.itemId] === 'back'
          ? `Stays off the list; the warehouse takes it in when ${first} brings it.`
          : note
            ? `“${note}”`
            : undefined;
    const where = posted ? (
      ext[e.itemId] === 'add' ? (
        `Put on ${place}`
      ) : (
        'Back to the shop'
      )
    ) : (
      <div className="fl-countrev__field" id={idOf('extra', e.itemId)}>
        <ChoiceGroup
          label={`${e.itemName}: where it goes`}
          selectionMode="single"
          value={ext[e.itemId] ?? ''}
          onChange={(v) => setExt((x) => ({ ...x, [e.itemId]: v as ExtraChoice }))}
        >
          <Choice value="add">Put it on {place}</Choice>
          <Choice value="back">{first} brings it to the shop</Choice>
        </ChoiceGroup>
        {errorFor(e.itemId) ? (
          <span className="fl-countrev__bad">
            <StateMark state="problem" />
            {errorFor(e.itemId)}
          </span>
        ) : null}
      </div>
    );
    return {
      id: `extra-${e.itemId}`,
      cells: {
        item: item(e.itemName, e.code),
        shows: { tone: 'none', value: '–' },
        counted: <strong>{qtyWords(e.counted, e.unit)}</strong>,
        off: offWords(Number(e.counted), e.unit),
        ...(narrow ? {} : { why: where }),
        ...(cost ? { cost: e.eachCents !== undefined ? signed(Number(e.counted) * e.eachCents) : null } : {}),
      },
      note: under(narrow ? where : null, said),
    };
  });

  const shown = all ? lines : lines.filter((l) => off(l) !== 0 || l.flagged);
  const groups: LineGroup[] = [];
  if (all) {
    const cats = new Map<string, LineGroup>();
    for (const l of shown) {
      const g = cats.get(l.category.id) ?? { id: l.category.id, label: l.category.name, rows: [] };
      g.rows.push(row(l));
      cats.set(l.category.id, g);
    }
    groups.push(...cats.values());
  }
  if (extraRows.length) groups.push({ id: 'extra', label: `Not on ${place}’s list`, rows: extraRows });
  const more = lines.length - shown.length;

  const short = diffs.reduce((s, l) => s + (l.eachCents ?? 0) * Math.min(0, off(l)), 0);
  const found = extra.reduce((s, e) => s + (e.eachCents ?? 0) * Number(e.counted), 0);
  // what the differences come to, for whoever sees cost: only what there is
  const costSays = !cost
    ? ''
    : short && found
      ? ` Short at cost: ${money(-short)}; found: ${money(found)}.`
      : short
        ? ` Short at cost: ${money(-short)}.`
        : found
          ? ` Found at cost: ${money(found)}.`
          : '';
  const asked = Object.keys(recount).filter((k) => diffs.some((l) => l.itemId === k));
  const reply = (
    <div className="fl-countrev__reply">
      <p className="fl-countrev__label">{posted ? `${first} sees` : `What ${first} sees when you post`}</p>
      <p className="fl-countrev__strong">
        {postedBy} posted your count{posted ? `, ${postedAt}` : ''}
      </p>
      <p className="fl-countrev__muted">
        {place} now shows what you counted.
        {asked.length ? ` ${asked.length === 1 ? 'One recount' : `${asked.length} recounts`} asked:` : ''}
      </p>
      {asked.map((k) => (
        <p key={k}>
          <strong>{lines.find((l) => l.itemId === k)!.itemName}</strong>: “{recount[k]}”
        </p>
      ))}
    </div>
  );
  return (
    <div className="fl-countrev">
      {posted ? (
        <Notice
          tone="done"
          takesFocus={!initial.postedAt}
          title={`Posted at ${postedAt}: ${place} shows what ${first} counted${
            asked.length
              ? `, except ${asked.map((k) => lines.find((l) => l.itemId === k)!.itemName).join(', ')} (recount asked)`
              : ''
          }.`}
        >
          <p>
            {extra
              .map((e) =>
                ext[e.itemId] === 'add'
                  ? `The ${e.itemName} is on ${place}’s list, with no min yet.`
                  : `The ${e.itemName} comes back to the shop.`,
              )
              .join(' ')}{' '}
            {first} sees your reply on the Truck tab.
          </p>
        </Notice>
      ) : (
        <Notice
          title={`${first}’s count arrived, ${sentAt}`}
          actions={
            <>
              <Button variant="primary" onPress={post}>
                Post the count
              </Button>
              {onNotNow ? <Link onPress={onNotNow}>Not now</Link> : null}
              {tried && problems[0] ? (
                <span className="fl-countrev__problem" role="alert">
                  <StateMark state="problem" />
                  {problems[0].msg}.
                </span>
              ) : null}
            </>
          }
        >
          <p>
            {diffs.length} of {lines.length} differ from what {place} shows
            {extra.length
              ? `, and ${extra.length} ${extra.length === 1 ? 'isn’t' : 'aren’t'} on its list`
              : ''}
            .{costSays} Posting sets on hand to what {first} counted; each difference needs a reason, or ask{' '}
            {first} to count it again.
          </p>
        </Notice>
      )}
      <LineTable
        label={`${tech}’s count beside what ${place} shows`}
        columns={columns}
        rows={all ? [] : shown.map(row)}
        groups={groups}
      />
      {more > 0 ? (
        <p className="fl-countrev__muted">
          {more} more match what it shows. <Link onPress={() => setAll(true)}>Show all {lines.length}</Link>
        </p>
      ) : all && lines.some((l) => off(l) === 0 && !l.flagged) ? (
        <p className="fl-countrev__muted">
          <Link onPress={() => setAll(false)}>Show only what differs</Link>
        </p>
      ) : null}
      <div className="fl-countrev__pair">
        {note ? (
          <div className="fl-countrev__quote">
            <p className="fl-countrev__label">{first}’s note</p>
            <p>“{note}”</p>
          </div>
        ) : null}
        {reply}
      </div>
    </div>
  );
}
